import type { ReactNode, SVGProps } from 'react';

import type { usePreferences } from '@knocklabs/react';
import { ToolsIcon, UsersBoldIcon } from '@truvhq/ui';
import { generatePath } from 'react-router-dom';

import type { CompanyType, Permissions, RoleId } from 'api/types';
import { TeamMemberStatus } from 'entities/team/lib/common';
import { urls } from 'shared/urls';

import DocumentBoldIcon from '../ui/icons/document-bold.svg?react';
import {
  DASHBOARD_POPUPS_PREFERENCE_KEY,
  type KnockChannelType,
  type LocalPreferences,
  NOTIFICATION_CATEGORIES,
  type NotificationCategory,
  type NotificationCategoryExtended,
} from './constants';

export const getNotificationLink = (
  category: NotificationCategory,
  item: { data?: { order_id?: string } | null },
): string | null => {
  switch (category) {
    case 'team': {
      return generatePath(urls.team.url(), { tab: TeamMemberStatus.WaitingForApproval });
    }
    case 'api': {
      return urls.keys.url();
    }
    case 'orders': {
      if (!item.data?.order_id) {
        return null;
      }

      return generatePath(urls.orderInformation.url(), { orderId: item.data.order_id });
    }
    default: {
      return null;
    }
  }
};

export const getRenderedContent = (blocks: unknown[]): string | null => {
  if (!blocks || blocks.length === 0) {
    return null;
  }
  const block = blocks[0] as { rendered?: string };
  return block.rendered ?? null;
};

export const getCategoryFromSource = (source: { categories?: string[] } | null): NotificationCategory => {
  if (!source?.categories?.length) {
    return 'all';
  }
  const categories = source.categories;
  if (categories.includes('team')) {
    return 'team';
  }
  if (categories.includes('api')) {
    return 'api';
  }

  return 'orders';
};

export const getCategoryIcon = (category: NotificationCategory): ReactNode => {
  const iconProps: SVGProps<SVGSVGElement> = { width: 20, height: 20, className: 'shrink-0' };

  switch (category) {
    case 'team': {
      return <UsersBoldIcon {...iconProps} />;
    }
    case 'api': {
      return <ToolsIcon {...iconProps} />;
    }
    case 'orders':
    case 'all': {
      return <DocumentBoldIcon {...iconProps} />;
    }
  }
};

type NotificationVisibilityParams = {
  permissions: Permissions | undefined;
  useCase: string | undefined;
  roleId: RoleId | undefined;
  companyType: CompanyType | undefined;
};

export const getVisibleNotificationCategories = ({
  permissions,
  useCase,
  roleId,
  companyType,
}: NotificationVisibilityParams): NotificationCategoryExtended[] => {
  if (!permissions) {
    return [];
  }
  return NOTIFICATION_CATEGORIES.map((category) => {
    const notifications = category.notifications.filter((notification) => {
      if (notification.visibleToPermission && !permissions.has(notification.visibleToPermission)) {
        return false;
      }
      if (
        notification.visibleToUseCases?.length &&
        (useCase == null || !notification.visibleToUseCases.some((uc) => uc === useCase))
      ) {
        return false;
      }
      if (notification.visibleToRoles?.length && (roleId == null || !notification.visibleToRoles.includes(roleId))) {
        return false;
      }
      if (
        notification.visibleToCompanyTypes?.length &&
        (companyType == null || !notification.visibleToCompanyTypes.includes(companyType))
      ) {
        return false;
      }
      return true;
    });
    return { ...category, notifications };
  }).filter((category) => category.notifications.length > 0);
};

type KnockPreferences = ReturnType<typeof usePreferences>['preferences'];

export const getChannelValueFromKnock = (
  knockPreferences: KnockPreferences,
  knockId: string,
  channel: KnockChannelType,
  type: 'category' | 'workflow',
): boolean => {
  const source = type === 'workflow' ? knockPreferences?.workflows : knockPreferences?.categories;
  const setting = source?.[knockId];

  if (setting === undefined) {
    return true;
  }

  if (typeof setting === 'boolean') {
    return setting;
  }

  if ('channel_types' in setting) {
    const value = setting.channel_types?.[channel];
    return typeof value === 'boolean' ? value : true;
  }

  return true;
};

export const buildLocalPreferencesFromKnock = (knockPreferences: KnockPreferences): LocalPreferences => {
  const local: LocalPreferences = {};
  for (const category of NOTIFICATION_CATEGORIES) {
    for (const notification of category.notifications) {
      const { channelTypeMap } = notification;
      local[notification.knockId] = {
        email: getChannelValueFromKnock(
          knockPreferences,
          notification.knockId,
          channelTypeMap?.email ?? 'email',
          notification.type,
        ),
        in_app_feed: getChannelValueFromKnock(
          knockPreferences,
          notification.knockId,
          channelTypeMap?.in_app_feed ?? 'in_app_feed',
          notification.type,
        ),
      };
    }
  }
  return local;
};

export const getDashboardPopupsEnabled = (knockPreferences: KnockPreferences): boolean => {
  const value = knockPreferences?.workflows?.[DASHBOARD_POPUPS_PREFERENCE_KEY];
  if (typeof value === 'boolean') {
    return value;
  }
  return true;
};
